import '@material/web/icon/icon.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'
import '@operato/board/ox-board-viewer.js'
import '../../components/data-entry-form.js'
import '../../components/checklist-entry-form.js'

import gql from 'graphql-tag'
import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'

import { connect } from 'pwa-helpers/connect-mixin'

import { ColumnConfig, DataGrist, FetchOption, SortersControl, getRenderer } from '@operato/data-grist'
import { OxDataUseCase } from '@operato/dataset'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { openPopup, notify } from '@operato/layout'
import { InheritedValueType, navigate, PageView, store } from '@operato/shell'
import { CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { provider } from '@things-factory/board-ui/dist-client/board-provider.js'

const USECASE_OPTIONS = () => {
  return ['', ...OxDataUseCase.getUseCaseNames()].map(name => {
    return {
      display: name,
      value: name
    }
  })
}

const showEntryView = async (columns, data, column, record, rowIndex) => {
  if (!record.isEntryAllowed) {
    notify({
      message: 'You are not allowed to enter data for this dataset',
      level: 'error'
    })
    return
  }

  const { name, entryType, entryView } = record
  const title = `${name} - ${i18next.t('title.data-entry-form')}`

  switch (entryType) {
    case 'generated':
      openPopup(html` <data-entry-form .dataSet=${record} style="background-color: white;"></data-entry-form> `, {
        closable: true,
        backdrop: true,
        size: 'large',
        title
      })
      break

    case 'checklist':
      openPopup(
        html` <checklist-entry-form .dataSet=${record} style="background-color: white;"></checklist-entry-form> `,
        {
          closable: true,
          backdrop: true,
          size: 'large',
          title
        }
      )
      break

    case 'board':
      const board = {
        id: entryView
      }
      openPopup(
        html`
          <ox-board-viewer
            style="background-color: white;"
            .board=${board}
            .provider=${provider}
            hide-fullscreen
            hide-navigation
          ></ox-board-viewer>
        `,
        {
          closable: true,
          backdrop: true,
          size: 'large',
          title
        }
      )

      // navigate(`board-viewer/${entryView}?interactive=true&title=${title}`)
      break

    case 'custom-element':
      break

    case 'page':
      navigate(`${entryView}/${record.id}`)
      break

    case 'external':
      window.open(entryView, '_blank')
      break
  }
}

const issueDataEntry = async (columns, data, column, record, rowIndex) => {
  if (!record.isSupervisor) {
    notify({
      message: 'You are not allowed to issue data collection for this dataset',
      level: 'error'
    })
    return
  }

  const response = await client.mutate({
    mutation: gql`
      mutation ($dataSetId: String!) {
        issueDataCollection(dataSetId: $dataSetId)
      }
    `,
    variables: { dataSetId: record.id }
  })

  if (response.errors) {
    notify({
      message: response.errors[0].message,
      level: 'error'
    })
  } else {
    notify({
      message: 'Data entry task issued successfully'
    })
  }
}

@customElement('data-entry-list-page')
export class DataEntryListPage extends connect(store)(localize(i18next)(PageView)) {
  static styles = [
    ScrollbarStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;

        width: 100%;

        --grid-record-emphasized-background-color: #8b0000;
        --grid-record-emphasized-color: #ff6b6b;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() private gristConfig: any

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-entry list'),
      search: {
        handler: (search: string) => {
          this.grist.searchText = search
        },
        value: this.grist?.searchText || ''
      },
      filter: {
        handler: () => {
          this.grist.toggleHeadroom()
        }
      },
      help: 'dataset/data-entry-list',
      toolbar: false
    }
  }

  render() {
    return html`
      <ox-grist
        mode="CARD"
        .config=${this.gristConfig}
        .fetchHandler=${this.fetchHandler.bind(this)}
        ?url-params-sensitive=${this.active}
      >
        <div slot="headroom" class="header">
          <div class="filters">
            <ox-filters-form autofocus without-search></ox-filters-form>
          </div>
        </div>
      </ox-grist>
    `
  }

  async pageInitialized(lifecycle) {
    this.gristConfig = {
      list: {
        thumbnail: 'entryView',
        fields: ['name', 'description'],
        details: ['schedule', 'type', 'useCase', 'latestCollectedAt', 'prevSchedule', 'nextSchedule']
      },
      columns: [
        {
          type: 'gutter',
          gutterName: 'button',
          icon: record => (record?.isSupervisor || record?.isEntryAllowed ? 'assignment_add' : ''),
          iconOnly: false,
          width: 96,
          fixed: true,
          title: record =>
            record?.isSupervisor || record?.isEntryAllowed ? i18next.t('button.assign-data-collection') : '',
          handlers: {
            click: issueDataEntry
          }
        },
        {
          type: 'gutter',
          gutterName: 'button',
          icon: record => (record?.isEntryAllowed ? 'note_alt' : ''),
          iconOnly: false,
          width: 96,
          fixed: true,
          title: record => (record?.isEntryAllowed ? i18next.t('button.enter-data') : ''),
          handlers: {
            click: showEntryView
          }
        },
        {
          type: 'string',
          name: 'name',
          header: i18next.t('field.name'),
          record: {
            editable: false
          },
          filter: 'search',
          sortable: true,
          width: 150
        },
        {
          type: 'string',
          name: 'description',
          header: i18next.t('field.description'),
          record: {
            editable: false
          },
          filter: 'search',
          width: 200
        },
        {
          type: 'select',
          name: 'type',
          label: true,
          header: i18next.t('field.type'),
          record: {
            editable: false,
            options: [
              {},
              {
                display: i18next.t('text.manually collected'),
                value: 'manual'
              },
              {
                display: i18next.t('text.automatically collected'),
                value: 'automatic'
              }
            ]
          },
          sortable: true,
          filter: true,
          width: 60
        },
        {
          type: 'select',
          name: 'useCase',
          label: true,
          header: i18next.t('field.use-case'),
          record: {
            editable: false,
            options: USECASE_OPTIONS
          },
          sortable: true,
          filter: {
            operator: 'eq',
            options: USECASE_OPTIONS /* in case select options type is a function, filter should have its own options */
          },
          width: 80
        },
        {
          type: 'crontab',
          name: 'schedule',
          label: true,
          header: i18next.t('field.schedule'),
          record: {
            editable: false
          },
          width: 80
        },
        {
          type: 'resource-object',
          name: 'entryRole',
          header: i18next.t('field.entry-role'),
          record: {
            editable: false
          },
          width: 120
        },
        {
          type: 'resource-object',
          name: 'supervisoryRole',
          header: i18next.t('field.supervisory-role'),
          record: {
            editable: false
          },
          width: 120
        },
        {
          type: 'datetime',
          name: 'latestCollectedAt',
          label: true,
          header: i18next.t('field.latest-collected-at'),
          record: {
            editable: false
          },
          width: 180
        },
        {
          type: 'datetime',
          name: 'prevSchedule',
          label: true,
          header: i18next.t('field.prev-schedule'),
          record: {
            editable: false
          },
          width: 180
        },
        {
          type: 'datetime',
          name: 'nextSchedule',
          label: true,
          header: i18next.t('field.next-schedule'),
          record: {
            editable: false
          },
          width: 180
        },
        {
          type: 'string',
          name: 'entryView',
          hidden: true,
          record: {
            editable: false,
            renderer: function (value, column, record, rowIndex, field) {
              const type = record.entryType !== 'board' ? 'string' : 'image'

              let renderedValue

              if (record.entryType === 'board' && record.entryBoard?.thumbnail) {
                renderedValue = record.entryBoard.thumbnail
              } else {
                renderedValue = html`<md-icon style="--md-icon-size: 64px;">assignment_add</md-icon>`
              }

              return getRenderer(type)(renderedValue, column, record, rowIndex, field)
            }
          },
          handlers: {
            click: (columns, data, column, record, rowIndex) => {
              if (record?.isEntryAllowed) {
                showEntryView(columns, data, column, record, rowIndex)
              }
            }
          }
        }
      ],
      rows: {
        appendable: false,
        editable: false,
        selectable: {
          multiple: false
        },
        classifier: function (record, rowIndex) {}
      },
      sorters: [
        {
          name: 'name'
        }
      ]
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    const response = await client.query({
      query: gql`
        query ($filters: [Filter!], $pagination: Pagination, $sortings: [Sorting!], $inherited: InheritedValueType) {
          responses: dataSetsForEntry(
            filters: $filters
            pagination: $pagination
            sortings: $sortings
            inherited: $inherited
          ) {
            items {
              id
              name
              description
              partitionKeys
              active
              type
              useCase
              schedule
              timezone
              isSupervisor
              isEntryAllowed
              entryRole {
                id
                name
              }
              supervisoryRole {
                id
                name
              }
              entryType
              entryView
              entryBoard {
                thumbnail
              }
              monitorType
              monitorView
              updater {
                id
                name
              }
              reportType
              reportView
              updatedAt
              dataItems {
                name
                description
                active
                hidden
                tag
                group
                subgroup
                type
                unit
                options
                quota
                spec
                stat
              }
              latestCollectedAt
              nextSchedule
              prevSchedule
              nextSummarySchedule
            }
            total
          }
        }
      `,
      variables: {
        filters,
        pagination: { page, limit },
        sortings,
        inherited: InheritedValueType.Include
      }
    })

    return {
      total: response.data.responses.total || 0,
      records: response.data.responses.items || []
    }
  }
}
